import { useState, useMemo, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { radioStations, type RadioStation } from "@/mocks/radios";
import { useAudioPlayer } from "@/context/AudioContext";
import Swal from "sweetalert2";

const STORAGE_KEY = "rfw_app_favorites";
const INITIAL_FAVORITE_IDS = [1, 3, 5, 7, 9, 13, 16, 19];

function loadFavorites(): number[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.every((v: unknown) => typeof v === "number")) {
        return parsed;
      }
    }
  } catch {
    /* corrupt storage */
  }
  return INITIAL_FAVORITE_IDS;
}

function saveFavorites(ids: number[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  } catch {
    /* storage full */
  }
}

export default function AppFavoritesPage() {
  const navigate = useNavigate();
  const { play, isPlaying, currentStation, pause } = useAudioPlayer();
  const [favoriteIds, setFavoriteIds] = useState<number[]>(() => loadFavorites());
  const [search, setSearch] = useState("");

  useEffect(() => {
    saveFavorites(favoriteIds);
  }, [favoriteIds]);

  const isFav = useCallback((id: number) => favoriteIds.includes(id), [favoriteIds]);

  const favStations = useMemo(() => {
    const list = radioStations.filter((s) => favoriteIds.includes(s.id));
    if (!search.trim()) return list;
    const q = search.toLowerCase().trim();
    return list.filter(
      (s) =>
        s.name.toLowerCase().includes(q) ||
        s.frequency.includes(q) ||
        s.category.toLowerCase().includes(q) ||
        s.city.toLowerCase().includes(q),
    );
  }, [favoriteIds, search]);

  const searchResults = useMemo(() => {
    if (!search.trim()) return [];
    const q = search.toLowerCase().trim();
    return radioStations.filter(
      (s) =>
        !favoriteIds.includes(s.id) &&
        (s.name.toLowerCase().includes(q) ||
          s.frequency.includes(q) ||
          s.category.toLowerCase().includes(q) ||
          s.city.toLowerCase().includes(q)),
    );
  }, [favoriteIds, search]);

  const handlePlay = useCallback(
    (e: React.MouseEvent, station: RadioStation) => {
      e.stopPropagation();
      const isThis = isPlaying && currentStation?.id === station.id;
      if (isThis) {
        pause();
        Swal.fire({
          toast: true,
          position: "top-end",
          title: "Reproducción pausada",
          icon: "info",
          showConfirmButton: false,
          timer: 1500,
          timerProgressBar: true,
          customClass: { popup: "rounded-xl", title: "text-sm font-medium" },
          buttonsStyling: false,
        });
      } else {
        play(station);
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "success",
          title: `Reproduciendo ${station.name}`,
          showConfirmButton: false,
          timer: 2000,
          timerProgressBar: true,
          customClass: { popup: "rounded-xl", title: "text-sm font-medium" },
          buttonsStyling: false,
        });
      }
    },
    [isPlaying, currentStation, play, pause],
  );

  const handleEnter = useCallback(
    (e: React.MouseEvent, station: RadioStation) => {
      e.stopPropagation();
      navigate(`/radio/${station.frequency.replace(".", "-")}`);
    },
    [navigate],
  );

  const handleAddFavorite = useCallback((e: React.MouseEvent, station: RadioStation) => {
    e.stopPropagation();
    setFavoriteIds((prev) => {
      if (prev.includes(station.id)) return prev;
      return [...prev, station.id];
    });
    Swal.fire({
      toast: true,
      position: "top-end",
      icon: "success",
      title: `"${station.name}" agregada a favoritas`,
      showConfirmButton: false,
      timer: 2000,
      timerProgressBar: true,
      customClass: { popup: "rounded-xl", title: "text-sm font-medium" },
      buttonsStyling: false,
    });
  }, []);

  const handleRemoveFavorite = useCallback((e: React.MouseEvent, station: RadioStation) => {
    e.stopPropagation();
    Swal.fire({
      title: "Quitar de favoritas",
      text: `¿Querés eliminar "${station.name}" de tus favoritas?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sí, quitar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#d1d5db",
      customClass: {
        popup: "rounded-2xl",
        title: "font-bold",
        confirmButton: "rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap",
        cancelButton: "rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap",
        htmlContainer: "text-sm",
      },
      buttonsStyling: false,
    }).then((result) => {
      if (result.isConfirmed) {
        setFavoriteIds((prev) => prev.filter((id) => id !== station.id));
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "success",
          title: `"${station.name}" eliminada de favoritas`,
          showConfirmButton: false,
          timer: 2000,
          timerProgressBar: true,
          customClass: { popup: "rounded-xl", title: "text-sm font-medium" },
          buttonsStyling: false,
        });
      }
    });
  }, []);

  const handleShare = useCallback(async (e: React.MouseEvent, station: RadioStation) => {
    e.stopPropagation();
    const url = `${window.location.origin}/radio/${station.frequency.replace(".", "-")}`;
    if (navigator.share) {
      try {
        await navigator.share({
          title: station.name,
          text: `${station.name} · ${station.frequency} WEB — Escuchando en RadioFrecuenciaWeb`,
          url,
        });
      } catch {
        /* user cancelled */
      }
    } else {
      await navigator.clipboard.writeText(url);
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "success",
        title: "Enlace copiado al portapapeles",
        showConfirmButton: false,
        timer: 1800,
        timerProgressBar: true,
        customClass: { popup: "rounded-xl", title: "text-sm font-medium" },
        buttonsStyling: false,
      });
    }
  }, []);

  const isEmpty = favoriteIds.length === 0;

  return (
    <div className="flex flex-col min-h-screen bg-background-50">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-background-50/95 backdrop-blur-sm border-b border-background-200 safe-top">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-3 flex items-center gap-3">
          <button
            onClick={() => navigate("/app")}
            className="w-9 h-9 rounded-xl bg-background-100 hover:bg-background-200 text-foreground-600 flex items-center justify-center cursor-pointer shrink-0 transition-all"
          >
            <i className="ri-arrow-left-line"></i>
          </button>
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-accent-500/15 flex items-center justify-center">
              <i className="ri-heart-fill text-accent-500 text-lg"></i>
            </div>
            <div>
              <h1 className="text-foreground-950 font-bold text-lg">Favoritas</h1>
              <p className="text-foreground-400 text-[11px]">
                {favoriteIds.length} {favoriteIds.length === 1 ? "estación guardada" : "estaciones guardadas"}
              </p>
            </div>
          </div>

          {favoriteIds.length > 0 && (
            <button
              onClick={() => {
                Swal.fire({
                  title: "Limpiar favoritas",
                  text: "¿Estás seguro? Se eliminarán todas tus radios favoritas.",
                  icon: "warning",
                  showCancelButton: true,
                  confirmButtonText: "Sí, limpiar todo",
                  cancelButtonText: "Cancelar",
                  confirmButtonColor: "#ef4444",
                  cancelButtonColor: "#d1d5db",
                  customClass: {
                    popup: "rounded-2xl",
                    title: "font-bold",
                    confirmButton: "rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap",
                    cancelButton: "rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap",
                    htmlContainer: "text-sm",
                  },
                  buttonsStyling: false,
                }).then((result) => {
                  if (result.isConfirmed) {
                    setFavoriteIds([]);
                    setSearch("");
                    Swal.fire({
                      toast: true,
                      position: "top-end",
                      icon: "success",
                      title: "Todas las favoritas fueron eliminadas",
                      showConfirmButton: false,
                      timer: 2000,
                      timerProgressBar: true,
                      customClass: { popup: "rounded-xl", title: "text-sm font-medium" },
                      buttonsStyling: false,
                    });
                  }
                });
              }}
              className="ml-auto text-xs text-foreground-400 hover:text-red-500 font-medium transition-colors whitespace-nowrap cursor-pointer"
            >
              Limpiar todas
            </button>
          )}
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 max-w-6xl mx-auto w-full px-4 md:px-6 pb-20">
        {/* Search bar — always visible */}
        <div className="sticky top-[57px] z-20 bg-background-50/95 backdrop-blur-sm pt-4 pb-3">
          <div className="relative">
            <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar radios para agregar a favoritas..."
              className="w-full pl-10 pr-10 py-2.5 bg-background-100 border border-background-200 rounded-xl text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-300 transition-colors"
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400 hover:text-foreground-600 cursor-pointer"
              >
                <i className="ri-close-line text-sm"></i>
              </button>
            )}
          </div>
        </div>

        {/* ── EMPTY STATE ── */}
        {isEmpty && !search.trim() && (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="w-24 h-24 rounded-full bg-accent-100 flex items-center justify-center mb-6">
              <i className="ri-heart-add-line text-accent-400 text-4xl"></i>
            </div>
            <h2 className="text-foreground-950 font-bold text-lg mb-2">No tienes radios favoritas</h2>
            <p className="text-foreground-500 text-sm max-w-xs mb-8">
              Tocá el corazón en cualquier estación para guardarla aquí y acceder rápido cuando quieras escucharla.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 w-full max-w-xs">
              <button
                onClick={() => navigate("/explorar")}
                className="flex-1 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-bold rounded-xl transition-all duration-200 whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
              >
                <i className="ri-compass-3-line"></i>
                Explorar radios
              </button>
              <button
                onClick={() => navigate("/app")}
                className="flex-1 py-3 bg-background-100 hover:bg-background-200 text-foreground-700 text-sm font-bold rounded-xl transition-all duration-200 whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 border border-background-200"
              >
                <i className="ri-radio-line"></i>
                Ir al dial
              </button>
            </div>
          </div>
        )}

        {/* ── FAVORITES LIST + SEARCH RESULTS ── */}
        {!isEmpty || search.trim() ? (
          <div className="mt-1 space-y-3">
            {/* Favorites section */}
            {favStations.length > 0 && (
              <div>
                {search.trim() && (
                  <div className="flex items-center gap-2 mb-3">
                    <h3 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider">
                      Mis favoritas ({favStations.length})
                    </h3>
                    <div className="flex-1 h-px bg-background-200"></div>
                  </div>
                )}
                <div className="space-y-2">
                  {favStations.map((station) => (
                    <FavoriteCard
                      key={station.id}
                      station={station}
                      isPlaying={isPlaying}
                      currentStation={currentStation}
                      onPlay={handlePlay}
                      onEnter={handleEnter}
                      onRemove={handleRemoveFavorite}
                      onShare={handleShare}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* No favorites match search */}
            {favStations.length === 0 && search.trim() && favoriteIds.length > 0 && (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mb-3">
                  <i className="ri-search-line text-foreground-300 text-xl"></i>
                </div>
                <p className="text-foreground-600 text-sm font-medium">Sin resultados en tus favoritas</p>
                <p className="text-foreground-400 text-xs mt-1">
                  Ninguna favorita coincide con &ldquo;{search}&rdquo;
                </p>
              </div>
            )}

            {/* Search results — add new favorites */}
            {searchResults.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3 mt-4">
                  <h3 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider">
                    Agregar a favoritas ({searchResults.length})
                  </h3>
                  <div className="flex-1 h-px bg-background-200"></div>
                </div>
                <div className="space-y-2">
                  {searchResults.slice(0, 10).map((station) => (
                    <div
                      key={station.id}
                      onClick={() => navigate(`/radio/${station.frequency.replace(".", "-")}`)}
                      className="flex items-center gap-3 p-3 rounded-xl bg-background-50 border border-background-200 hover:border-foreground-300 transition-colors cursor-pointer group"
                    >
                      <div className="w-11 h-11 rounded-xl overflow-hidden bg-background-100 shrink-0">
                        <img
                          src={station.logoUrl}
                          alt={station.name}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h4 className="text-foreground-950 font-semibold text-sm truncate">{station.name}</h4>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-foreground-500 text-[11px] whitespace-nowrap">
                            {station.frequency}
                          </span>
                          <span className="w-1 h-1 rounded-full bg-foreground-300 shrink-0"></span>
                          <span className="text-foreground-500 text-[11px]">{station.category}</span>
                          <span className="w-1 h-1 rounded-full bg-foreground-300 shrink-0"></span>
                          <span className="text-foreground-500 text-[11px]">{station.city}</span>
                        </div>
                      </div>
                      <button
                        onClick={(e) => handleAddFavorite(e, station)}
                        className="w-9 h-9 rounded-lg flex items-center justify-center cursor-pointer bg-accent-500 hover:bg-accent-600 text-background-50 transition-all shrink-0"
                        title="Agregar a favoritas"
                      >
                        <i className="ri-heart-add-line text-sm"></i>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Search with no results at all */}
            {favStations.length === 0 && searchResults.length === 0 && search.trim() && (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mb-4">
                  <i className="ri-search-line text-foreground-300 text-2xl"></i>
                </div>
                <p className="text-foreground-600 text-sm font-medium">Sin resultados</p>
                <p className="text-foreground-400 text-xs mt-1">
                  No se encontraron radios que coincidan con &ldquo;{search}&rdquo;
                </p>
                <button
                  onClick={() => setSearch("")}
                  className="mt-4 px-4 py-2 bg-background-100 hover:bg-background-200 text-foreground-600 text-xs font-semibold rounded-xl cursor-pointer whitespace-nowrap transition-all"
                >
                  Limpiar búsqueda
                </button>
              </div>
            )}
          </div>
        ) : null}
      </main>
    </div>
  );
}

/* ── Favorite Card Sub-component ── */

function FavoriteCard({
  station,
  isPlaying,
  currentStation,
  onPlay,
  onEnter,
  onRemove,
  onShare,
}: {
  station: RadioStation;
  isPlaying: boolean;
  currentStation: RadioStation | null;
  onPlay: (e: React.MouseEvent, s: RadioStation) => void;
  onEnter: (e: React.MouseEvent, s: RadioStation) => void;
  onRemove: (e: React.MouseEvent, s: RadioStation) => void;
  onShare: (e: React.MouseEvent, s: RadioStation) => void;
}) {
  const isThisPlaying = isPlaying && currentStation?.id === station.id;

  return (
    <div className="group bg-background-50 border border-background-200 hover:border-foreground-300 rounded-2xl p-4 transition-all duration-200">
      <div className="flex items-center gap-4">
        {/* Logo */}
        <div className="relative shrink-0">
          <img
            src={station.logoUrl}
            alt={station.name}
            className="w-14 h-14 rounded-xl object-cover"
            loading="lazy"
          />
          {station.isLive && (
            <div className="absolute -top-1 -right-1 w-4 h-4 bg-accent-500 rounded-full border-2 border-background-50 flex items-center justify-center">
              <div className="w-1.5 h-1.5 bg-background-50 rounded-full animate-pulse"></div>
            </div>
          )}
        </div>

        {/* Info */}
        <div className="min-w-0 flex-1">
          <h3 className="text-foreground-950 font-bold text-sm truncate">{station.name}</h3>
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            <span className="text-foreground-500 text-xs whitespace-nowrap">{station.frequency}</span>
            <span className="w-1 h-1 rounded-full bg-foreground-300 shrink-0"></span>
            <span className="text-foreground-500 text-xs truncate">{station.category}</span>
          </div>
          <div className="flex items-center gap-3 mt-1.5">
            <div className="flex items-center gap-1">
              <i className="ri-headphone-line text-foreground-400 text-[10px]"></i>
              <span className="text-foreground-500 text-[11px] font-medium">
                {station.listeners >= 1000 ? `${(station.listeners / 1000).toFixed(1)}K` : station.listeners}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <i className="ri-star-fill text-amber-500 text-[10px]"></i>
              <span className="text-foreground-500 text-[11px] font-medium">{station.rating}</span>
            </div>
            {station.isLive && (
              <div className="flex items-center gap-1 bg-accent-100 px-1.5 py-0.5 rounded-full">
                <div className="w-1.5 h-1.5 bg-accent-500 rounded-full animate-pulse"></div>
                <span className="text-accent-700 text-[10px] font-bold whitespace-nowrap">EN VIVO</span>
              </div>
            )}
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-1.5 shrink-0">
          {/* Play */}
          <button
            onClick={(e) => onPlay(e, station)}
            className={`w-10 h-10 rounded-xl flex items-center justify-center cursor-pointer transition-all ${
              isThisPlaying
                ? "bg-accent-500 text-background-50"
                : "bg-primary-500 text-background-50 hover:bg-primary-600"
            }`}
            title={isThisPlaying ? "Pausar" : "Reproducir"}
          >
            <i className={`text-base ${isThisPlaying ? "ri-pause-fill" : "ri-play-fill"}`}></i>
          </button>

          {/* Enter */}
          <button
            onClick={(e) => onEnter(e, station)}
            className="w-10 h-10 rounded-xl flex items-center justify-center cursor-pointer bg-background-100 text-foreground-500 hover:bg-background-200 hover:text-foreground-700 transition-all"
            title="Entrar a la radio"
          >
            <i className="ri-arrow-right-line text-sm"></i>
          </button>

          {/* Share */}
          <button
            onClick={(e) => onShare(e, station)}
            className="w-10 h-10 rounded-xl flex items-center justify-center cursor-pointer bg-background-100 text-foreground-400 hover:bg-background-200 hover:text-foreground-600 transition-all"
            title="Compartir"
          >
            <i className="ri-share-forward-line text-sm"></i>
          </button>

          {/* Remove from favorites */}
          <button
            onClick={(e) => onRemove(e, station)}
            className="w-10 h-10 rounded-xl flex items-center justify-center cursor-pointer text-accent-500 hover:bg-accent-50 transition-all"
            title="Quitar de favoritas"
          >
            <i className="ri-heart-fill text-lg"></i>
          </button>
        </div>
      </div>
    </div>
  );
}